import { useContext, useEffect, useState } from 'react';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import { useConfigService, useStoreId, ExperienceContext } from '@thd-nucleus/experience-context';
import { createActionId } from '../helpers';

/**
 * @typedef {Object} UserFeedbackReturn
 * @property {boolean} feedbackSubmitted - Indicates if feedback has been submitted.
 * @property {Function} handleSelectOption - Function to handle option selection.
 * @property {string[]} selectedOptionReasons - Reasons for the selected feedback option.
 * @property {boolean} showFeedbackForm - Indicates if the feedback form is shown.
 * @property {Function} handleFeedbackFormClose - Function to handle the feedback form close.
 * @property {Function} handleFeedbackFormSubmit - Function to handle the feedback form submission.
 */
/**
 * Hook to manage feedback form state and submission.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.anchorKey - Key for the feedback anchor.
 * @param {string} params.anchorType - Type of the feedback anchor.
 * @param {boolean} params.skipFeedbackForm - Flag to skip the feedback form.
 * @param {Object} params.reasons - Reasons for feedback. { POSITIVE: string[], NEGATIVE: string[] }
 * @param {Object} params.customerInsights - Customer insights name.
 * @param {Function} params.handleFeedbackData - Function to handle feedback data.
 * @param {Object} params.feedbackAnalytics - Analytics data for feedback event triggers.
 * @returns { UserFeedbackReturn } - Returns an object containing feedback form state and handlers.
 */
export const useUserFeedback = ({
  anchorKey,
  anchorType,
  skipFeedbackForm,
  reasons,
  customerInsights,
  handleFeedbackData,
  feedbackAnalytics,
  forceCloseForm,
}) => {
  const storeId = useStoreId();
  const { deliveryZipCode, channel, isConsumerApp } = useContext(ExperienceContext);
  const [optionSelected, setOptionSelected] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [selectedOptionReasons, setSelectedOptionReasons] = useState([]);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [submitUserFeedback, response] = useLazyDataModel('captureFeedback', {
    lazy: true
  });

  const llmFromQABotFS = useConfigService('fs-prop:magic-apron-bot-llm');
  const llmFromMAABotFS = useConfigService('fs:magic-apron-assistant-bot-llm');
  const llmFromFS = customerInsights === 'Assistant' ? llmFromMAABotFS : llmFromQABotFS || '';

  const getFeedbackData = (formData) => {
    const visitorId = window?.THDCustomer?.default?.mcvisID;

    return {
      feedback: {
        feedbackComment: '',
        feedbackReason: '',
        modelName: llmFromFS,
        ...formData,
        storeId,
        visitorId,
        zipCode: deliveryZipCode,
        deviceType: isConsumerApp ? 'app' : channel,
      }
    };
  };

  const triggerFeedbackClickAnalytics = ({ analyticsActionID, optionSelected }) => {
    const _currentFeedbackAnalytics = { ...feedbackAnalytics };
    const origin = _currentFeedbackAnalytics.feedbackClickCommentOrigin || '';
    _currentFeedbackAnalytics.eventName = _currentFeedbackAnalytics.eventName + " click";
    _currentFeedbackAnalytics.actionID = analyticsActionID;
    _currentFeedbackAnalytics.feedback = `${origin}:${optionSelected === 'POSITIVE' ? 'thumbs up' : 'thumbs down'}`;
    window.parent.LIFE_CYCLE_EVENT_BUS.trigger('user-feedback.click', _currentFeedbackAnalytics);
  };

  const triggerFeedbackCommentAnalytics = ({ analyticsActionID, data }) => {
    const { feedbackReason, feedbackComment } = data;
    const _currentFeedbackAnalytics = { ...feedbackAnalytics };
    const origin = _currentFeedbackAnalytics.feedbackClickCommentOrigin || '';
    _currentFeedbackAnalytics.eventName = _currentFeedbackAnalytics.eventName + " comment submission";
    _currentFeedbackAnalytics.actionID = analyticsActionID;
    _currentFeedbackAnalytics.feedbackComment = `${origin}:${feedbackReason}|${feedbackComment}`;
    window.parent.LIFE_CYCLE_EVENT_BUS.trigger('user-feedback.comment', _currentFeedbackAnalytics);
  };

  const submitFeedbackData = (feedback) => {
    const dataToSubmit = {
      ...feedback,
      anchorKey,
      anchorType,
      customerInsights,
      content: handleFeedbackData()
    };
    submitUserFeedback({ variables: getFeedbackData(dataToSubmit) });
    if (response) {
      setFeedbackSubmitted(true);
    }
  };

  const handleSelectOption = (option) => setOptionSelected(option);

  const handleAfterThumbsUpThumbsDownSelected = () => {
    // Note: analyticsActionID contains a timestamp that has to be sent to both the analytics trigger and the request
    const analyticsActionID = createActionId(feedbackAnalytics.sessionID, feedbackAnalytics.fallbackSessionID);
    triggerFeedbackClickAnalytics({ optionSelected, analyticsActionID });
    if (reasons[optionSelected].length === 0 || skipFeedbackForm) {
      submitFeedbackData({ feedbackType: optionSelected });
      setFeedbackSubmitted(true);
    }

    setSelectedOptionReasons(reasons[optionSelected]);
    setShowFeedbackForm(true);
  };

  const handleFeedbackFormClose = () => {
    setShowFeedbackForm(false);
    submitFeedbackData({ feedbackType: optionSelected });
    setFeedbackSubmitted(true);
  };

  const handleFeedbackFormSubmit = (data) => {
    // Note: analyticsActionID contains a timestamp that has to be sent to both the analytics trigger and the request
    const analyticsActionID = createActionId(feedbackAnalytics.sessionID, feedbackAnalytics.fallbackSessionID);
    submitFeedbackData({ feedbackType: optionSelected, ...data });
    triggerFeedbackCommentAnalytics({ analyticsActionID, data });
    setFeedbackSubmitted(true);
  };

  useEffect(() => {
    if ((optionSelected)) handleAfterThumbsUpThumbsDownSelected();
  }, [optionSelected]);

  useEffect(() => {
    // Close form if drawer is minimized
    if (forceCloseForm && showFeedbackForm) {
      handleFeedbackFormClose();
    }
  }, [forceCloseForm, showFeedbackForm]);

  return {
    feedbackSubmitted,
    handleSelectOption,
    selectedOptionReasons,
    showFeedbackForm,
    handleFeedbackFormClose,
    handleFeedbackFormSubmit,
  };
};
